// src/pages/AdminLaundryPage.js
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert, Button, Form, Modal, Spinner, Table,
} from "react-bootstrap";
import api from "../api";

// reportlab named colors (also valid CSS names, so they render as swatches).
// Mirrors the palette laundry_calendar.py understands, plus a few extras.
const COLOR_OPTIONS = [
  "darkgreen",
  "darkblue",
  "magenta",
  "darkred",
  "purple",
  "navy",
  "teal",
  "darkorange",
  "maroon",
  "black",
];

const WEEKDAYS = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

// Which occurrence of the weekday within the month (1st..4th).
const WEEK_OPTIONS = [
  { value: "1", label: "1st" },
  { value: "2", label: "2nd" },
  { value: "3", label: "3rd" },
  { value: "4", label: "4th" },
];

const EMPTY_FORM = {
  laundromat_name: "",
  city: "",
  color: "darkgreen",
  day_of_week: "Wednesday",
  week_number: "3",
  laundromat_address: "",
  laundromat_phone: "",
  laundromat_contact: "",
};

function scheduleLabel(item) {
  const wk = WEEK_OPTIONS.find((w) => String(w.value) === String(item.week_number));
  if (!wk || !item.day_of_week) return null;
  return `${wk.label} ${item.day_of_week}`;
}

function ColorSwatch({ color }) {
  if (!color) return <span className="fst-italic text-muted">—</span>;
  return (
    <span className="d-inline-flex align-items-center gap-2">
      <span
        style={{
          display: "inline-block",
          width: 14,
          height: 14,
          borderRadius: 3,
          backgroundColor: color,
          border: "1px solid rgba(0,0,0,0.2)",
        }}
      />
      {color}
    </span>
  );
}

export default function AdminLaundryPage() {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState("");

  const [modal, setModal]   = useState(null); // "add" | "edit" | "delete"
  const [target, setTarget] = useState(null);
  const [form, setForm]     = useState(EMPTY_FORM);
  const [actionError, setActionError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api.get("/admin/laundry-ministry");
      setItems(res.data.items);
    } catch {
      setError("Failed to load laundromats.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function field(key) {
    return (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  }

  function openAdd() {
    setForm(EMPTY_FORM);
    setActionError("");
    setTarget(null);
    setModal("add");
  }

  function openEdit(item) {
    setForm({
      laundromat_name:    item.laundromat_name || "",
      city:               item.city || "",
      color:              item.color || "darkgreen",
      day_of_week:        item.day_of_week || "Wednesday",
      week_number:        item.week_number ? String(item.week_number) : "3",
      laundromat_address: item.laundromat_address || "",
      laundromat_phone:   item.laundromat_phone || "",
      laundromat_contact: item.laundromat_contact || "",
    });
    setActionError("");
    setTarget(item);
    setModal("edit");
  }

  function openDelete(item) {
    setTarget(item);
    setActionError("");
    setModal("delete");
  }

  function closeModal() {
    setModal(null);
    setActionError("");
  }

  function validate() {
    if (!form.laundromat_name.trim()) { setActionError("Laundromat is required."); return false; }
    if (!form.city.trim())  { setActionError("City is required."); return false; }
    if (!form.color.trim()) { setActionError("Color is required."); return false; }
    if (!form.day_of_week)  { setActionError("Day of week is required."); return false; }
    if (!form.week_number)  { setActionError("Week of month is required."); return false; }
    return true;
  }

  function payload() {
    return {
      laundromat_name:    form.laundromat_name.trim(),
      city:               form.city.trim(),
      color:              form.color.trim(),
      day_of_week:        form.day_of_week,
      week_number:        Number(form.week_number),
      laundromat_address: form.laundromat_address.trim(),
      laundromat_phone:   form.laundromat_phone.trim(),
      laundromat_contact: form.laundromat_contact.trim(),
    };
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setActionError("");
    try {
      await api.post("/admin/laundry-ministry", payload());
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to create laundromat.");
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit(e) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setActionError("");
    try {
      await api.put(`/admin/laundry-ministry/${target.id}`, payload());
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to update laundromat.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    setActionError("");
    try {
      await api.delete(`/admin/laundry-ministry/${target.id}`);
      closeModal();
      await load();
    } catch {
      setActionError("Failed to delete laundromat.");
    } finally {
      setDeleting(false);
    }
  }

  function renderFields(idPrefix) {
    return (
      <>
        <Form.Group className="mb-3">
          <Form.Label>Laundromat <span className="text-danger">*</span></Form.Label>
          <Form.Control value={form.laundromat_name} onChange={field("laundromat_name")} placeholder="e.g. Clare Hometown Laundry" autoFocus />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>City <span className="text-danger">*</span></Form.Label>
          <Form.Control value={form.city} onChange={field("city")} placeholder="Clare" />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Color <span className="text-danger">*</span></Form.Label>
          <div className="d-flex align-items-center gap-2">
            <Form.Select id={`${idPrefix}-color`} value={form.color} onChange={field("color")}>
              {COLOR_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Form.Select>
            <span
              style={{
                display: "inline-block",
                width: 24,
                height: 24,
                flex: "0 0 auto",
                borderRadius: 4,
                backgroundColor: form.color,
                border: "1px solid rgba(0,0,0,0.2)",
              }}
            />
          </div>
          <Form.Text className="text-muted">Highlight color used on the printed calendar.</Form.Text>
        </Form.Group>
        <div className="row g-2 mb-3">
          <div className="col-sm-6">
            <Form.Group>
              <Form.Label>Week of Month <span className="text-danger">*</span></Form.Label>
              <Form.Select id={`${idPrefix}-week`} value={form.week_number} onChange={field("week_number")}>
                {WEEK_OPTIONS.map((w) => (
                  <option key={w.value} value={w.value}>{w.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-sm-6">
            <Form.Group>
              <Form.Label>Day of Week <span className="text-danger">*</span></Form.Label>
              <Form.Select id={`${idPrefix}-dow`} value={form.day_of_week} onChange={field("day_of_week")}>
                {WEEKDAYS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-12">
            <Form.Text className="text-muted">
              The ministry runs the {WEEK_OPTIONS.find((w) => w.value === String(form.week_number))?.label || ""}{" "}
              {form.day_of_week} of each month.
            </Form.Text>
          </div>
        </div>
        <Form.Group className="mb-3">
          <Form.Label>Address</Form.Label>
          <Form.Control value={form.laundromat_address} onChange={field("laundromat_address")} placeholder="123 Main St" />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Phone</Form.Label>
          <Form.Control value={form.laundromat_phone} onChange={field("laundromat_phone")} placeholder="989-539-2064" />
        </Form.Group>
        <Form.Group>
          <Form.Label>Contact Person</Form.Label>
          <Form.Control value={form.laundromat_contact} onChange={field("laundromat_contact")} placeholder="e.g. Jane Smith" />
        </Form.Group>
      </>
    );
  }

  if (loading) {
    return (
      <div className="p-3">
        <Spinner animation="border" size="sm" className="me-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="d-flex align-items-center mb-4 gap-3">
        <Link to="/admin" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1" />Back
        </Link>
        <h3 className="mb-0">
          <i className="bi bi-basket2-fill me-2" />
          Laundry Ministry
        </h3>
        <Button size="sm" className="ms-auto" onClick={openAdd}>
          <i className="bi bi-plus-lg me-1" />Add Laundromat
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {items.length === 0 && !error ? (
        <p className="text-muted">No laundromats have been added yet.</p>
      ) : (
        <Table hover responsive size="sm">
          <thead>
            <tr>
              <th>Laundromat</th>
              <th>City</th>
              <th>Schedule</th>
              <th>Color</th>
              <th>Address</th>
              <th>Phone</th>
              <th>Contact Person</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td className="align-middle fw-semibold">{item.laundromat_name || <span className="fst-italic text-muted">—</span>}</td>
                <td className="align-middle text-muted">{item.city || <span className="fst-italic">—</span>}</td>
                <td className="align-middle text-muted">{scheduleLabel(item) || <span className="fst-italic">—</span>}</td>
                <td className="align-middle text-muted"><ColorSwatch color={item.color} /></td>
                <td className="align-middle text-muted">{item.laundromat_address || <span className="fst-italic">—</span>}</td>
                <td className="align-middle text-muted">{item.laundromat_phone || <span className="fst-italic">—</span>}</td>
                <td className="align-middle text-muted">{item.laundromat_contact || <span className="fst-italic">—</span>}</td>
                <td className="align-middle">
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-primary" onClick={() => openEdit(item)}>Edit</Button>
                    <Button size="sm" variant="outline-danger"  onClick={() => openDelete(item)}>Delete</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Add Modal */}
      <Modal show={modal === "add"} onHide={closeModal} centered>
        <Form onSubmit={handleAdd}>
          <Modal.Header closeButton>
            <Modal.Title>Add Laundromat</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
            {renderFields("add")}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Add Laundromat"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal show={modal === "edit"} onHide={closeModal} centered>
        <Form onSubmit={handleEdit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Laundromat</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
            {renderFields("edit")}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={modal === "delete"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Laundromat</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
          <p>
            Delete <strong>{target?.laundromat_name || "this laundromat"}</strong>
            {target?.city ? ` in ${target.city}` : ""}?
          </p>
          <p className="text-danger mb-0">This cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner animation="border" size="sm" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
